import { EncounterType } from '@pm/graphql';
import { Address, Icon, IconName, Text, TextLink } from '@pm/ui';
import { useTranslation } from 'react-i18next';
import { EncounterDetail } from './EncounterDetail';

type EncounterTypeDisplayProps = {
  encounterType: EncounterType;
  clinicName?: string;
  clinicAddress?: {
    street: string;
    city: string;
    province: string;
    postalCode: string;
  };
  clinicGoogleMapsLink?: string;
};

export type EncounterTypeKey = 'Video' | 'Phone' | 'Message' | 'InPerson';

export const EncounterTypeKeyForType: Record<EncounterType, EncounterTypeKey> =
  {
    [EncounterType.Video]: 'Video',
    [EncounterType.Phone]: 'Phone',
    [EncounterType.Message]: 'Message',
    [EncounterType.InPerson]: 'InPerson',
  };

export const EncounterTypeIcon: Record<EncounterType, IconName> = {
  [EncounterType.Message]: 'mail',
  [EncounterType.Phone]: 'call',
  [EncounterType.Video]: 'videocam',
  [EncounterType.InPerson]: 'location_on',
};

export const EncounterTypeDisplay = ({
  encounterType,
  clinicName,
  clinicAddress,
  clinicGoogleMapsLink,
}: EncounterTypeDisplayProps) => {
  const { t } = useTranslation('encounters', {
    keyPrefix: 'EncounterTypes',
  });
  const encounterTypeKey = EncounterTypeKeyForType[encounterType];
  const isInPersonWithClinic =
    encounterType === EncounterType.InPerson && clinicName;

  const encounterTypeText = isInPersonWithClinic
    ? (t('InPersonWithClinic', { clinicName }) as string)
    : (t(`${encounterTypeKey}`) as string);

  return (
    <EncounterDetail iconName={EncounterTypeIcon[encounterType]}>
      {clinicAddress ? (
        <div className="space-y-1">
          <Text size="bodyL">{encounterTypeText}</Text>
          <div className="text-body-l">
            <Address
              street={clinicAddress.street}
              city={clinicAddress.city}
              province={clinicAddress.province}
              postalCode={clinicAddress.postalCode}
            />
          </div>
          {clinicGoogleMapsLink && (
            <TextLink
              external
              to={clinicGoogleMapsLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              <div className="inline-flex items-center gap-xs">
                <Text size="bodyS">{t('OpenInGoogleMaps')}</Text>
                <Icon name="open_in_new" size="medium" />
              </div>
            </TextLink>
          )}
        </div>
      ) : (
        <Text size="bodyL">{encounterTypeText}</Text>
      )}
    </EncounterDetail>
  );
};
